// Public forgot-password form. Posts to /password-reset/request, which
// always returns 200 — we never reveal whether the email matched a real
// account. The submit confirmation is intentionally generic for that
// reason.
import { useCallback, useState } from "react";
import { ApiError, httpClient } from "../../client/http-client";

const cardClassName =
	"tg-surface tg-text-primary w-full max-w-[560px] rounded-[18px] p-6 shadow-[var(--shadow-tg-elevated)]";
const inputClassName =
	"tg-input tg-text-primary h-12 w-full rounded-[16px] px-4";
const primaryButtonClassName =
	"tg-btn-primary inline-flex h-10 items-center justify-center rounded-xl px-4 text-[0.95rem] font-semibold disabled:cursor-wait disabled:opacity-60";
const linkButtonClassName =
	"tg-text-secondary hover:tg-text-primary cursor-pointer text-[0.85rem] font-semibold underline-offset-4 hover:underline";

interface ForgotPasswordPageProps {
	onBackToLogin: () => void;
}

export const ForgotPasswordPage = ({
	onBackToLogin,
}: ForgotPasswordPageProps) => {
	const [email, setEmail] = useState("");
	const [busy, setBusy] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = useCallback(async () => {
		setError(null);
		const trimmed = email.trim();
		if (!trimmed) {
			setError("Enter the email for your account.");
			return;
		}
		setBusy(true);
		try {
			await httpClient.auth.passwordReset.request(trimmed);
			setSubmitted(true);
		} catch (err) {
			setError(
				err instanceof ApiError
					? err.message
					: err instanceof Error
						? err.message
						: String(err),
			);
		} finally {
			setBusy(false);
		}
	}, [email]);

	if (submitted) {
		return (
			<section className={cardClassName}>
				<div>
					<span className="tg-text-secondary mb-2 inline-flex text-[0.72rem] font-semibold uppercase tracking-[0.13em]">
						Check your inbox
					</span>
					<h2 className="tg-text-primary max-w-[14ch] text-[clamp(1.95rem,2.8vw,2.8rem)] leading-[1.06] font-bold">
						Reset link sent
					</h2>
					<p className="tg-text-muted mt-2.5 max-w-[46ch] text-[0.95rem] leading-7">
						If an account exists for that email, we’ve sent a password reset
						link. The link expires in 1 hour. Don’t see it? Check your spam
						folder, or try again.
					</p>
				</div>
				<div className="mt-5">
					<button
						type="button"
						onClick={onBackToLogin}
						className={primaryButtonClassName}
					>
						Back to sign in
					</button>
				</div>
			</section>
		);
	}

	return (
		<section className={cardClassName}>
			<div>
				<span className="tg-text-secondary mb-2 inline-flex text-[0.72rem] font-semibold uppercase tracking-[0.13em]">
					Forgot your password?
				</span>
				<h2 className="tg-text-primary max-w-[14ch] text-[clamp(1.95rem,2.8vw,2.8rem)] leading-[1.06] font-bold">
					Reset password
				</h2>
				<p className="tg-text-muted mt-2.5 max-w-[46ch] text-[0.95rem] leading-7">
					Enter the email associated with your Signal Lens account. We’ll
					send you a link to choose a new password.
				</p>
			</div>

			<form
				className="mt-5 grid gap-3.5"
				onSubmit={(event) => {
					event.preventDefault();
					void handleSubmit();
				}}
			>
				<label className="grid gap-2">
					<span className="tg-text-primary text-[0.92rem] font-semibold">
						Email
					</span>
					<input
						className={inputClassName}
						autoFocus
						value={email}
						onChange={(event) => setEmail(event.target.value)}
						placeholder="name@example.com"
						type="email"
						inputMode="email"
						autoComplete="email"
						disabled={busy}
					/>
				</label>

				<button
					className={primaryButtonClassName}
					type="submit"
					disabled={busy}
				>
					{busy ? "Sending…" : "Send reset link"}
				</button>
			</form>

			{error ? (
				<div className="tg-alert-error mt-4 rounded-2xl px-4 py-3" role="alert">
					{error}
				</div>
			) : null}

			<p className="tg-text-muted mt-5 text-[0.82rem] leading-6">
				<button
					type="button"
					className={linkButtonClassName}
					onClick={onBackToLogin}
				>
					Back to sign in
				</button>
			</p>
		</section>
	);
};
